import type { CompositeFilterDescriptor, DataSourceRequestState, SortDescriptor } from "@progress/kendo-data-query";
import { DatePicker, type DatePickerChangeEvent } from "@progress/kendo-react-dateinputs";
import { DropDownList, type DropDownListChangeEvent } from "@progress/kendo-react-dropdowns";
import { GridColumn, GridNoRecords, type GridCellProps, type GridDataStateChangeEvent, type GridPageChangeEvent, type GridSortChangeEvent } from "@progress/kendo-react-grid";
import { skipToken } from "@reduxjs/toolkit/query";
import { useState } from "react";
import { useListCustomersQuery } from "../../api/admin/adminCustomerApi";
import { useListProductsQuery } from "../../api/admin/adminProductApi";
import { useListAdminRawSaleRecordsQuery } from "../../api/admin/adminRawSaleRecordApi";
import { useListRetailersQuery } from "../../api/admin/adminRetailerApi";
import AppGrid from "../../components/grid/appGrid";
import type { CustomerDto, ProductDto, RawSaleRecordDto, RetailerDto } from "../../contracts";
import { formatDate, toDate } from "../../utils/dateValueConversion";
import { formatCurrency, formatNumber } from "../../utils/numberValueConversion";

const initialSort: SortDescriptor[] = [{ field: "fromDate", dir: "desc" }];
const selectorSort: SortDescriptor[] = [{ field: "name", dir: "asc" }];
const selectorRequest: DataSourceRequestState = { skip: 0, take: 1000, sort: selectorSort };

const buildFilter = (selectedCustomer: CustomerDto,
    selectedRetailer: RetailerDto | null,
    selectedProduct: ProductDto | null,
    fromDate: Date | null,
    toDateValue: Date | null,
    dataStateFilter?: CompositeFilterDescriptor): CompositeFilterDescriptor => ({
    logic: "and",
    filters: [
        ...(dataStateFilter == null ? [] : [dataStateFilter]),
        { field: "customerId", operator: "eq", value: selectedCustomer.id },
        ...(selectedRetailer == null ? [] : [{ field: "retailerId", operator: "eq", value: selectedRetailer.id }]),
        ...(selectedProduct == null ? [] : [{ field: "productId", operator: "eq", value: selectedProduct.id }]),
        ...(fromDate == null ? [] : [{ field: "toDate", operator: "gte", value: fromDate }]),
        ...(toDateValue == null ? [] : [{ field: "fromDate", operator: "lte", value: toDateValue }])
    ]
});

const buildRawSaleRecordRequest = (dataState: DataSourceRequestState, selectedCustomer: CustomerDto | null, selectedRetailer: RetailerDto | null, selectedProduct: ProductDto | null, fromDate: Date | null, toDateValue: Date | null): DataSourceRequestState | typeof skipToken => {
    if (selectedCustomer == null) {
        return skipToken;
    }

    return {
        ...dataState,
        filter: buildFilter(selectedCustomer, selectedRetailer, selectedProduct, fromDate, toDateValue, dataState.filter)
    };
};

const TextCell = (field: keyof RawSaleRecordDto): React.FC<GridCellProps> => props => {
    const record = props.dataItem as RawSaleRecordDto;
    const value = record[field];

    return <td className="k-table-td">{typeof value === "string" && value.trim() !== "" ? value : "-"}</td>;
};

const DateCell = (field: keyof RawSaleRecordDto): React.FC<GridCellProps> => props => {
    const record = props.dataItem as RawSaleRecordDto;
    const value = toDate(record[field] as Date | string | null | undefined);

    return <td className="k-table-td">{value == null ? "-" : formatDate(value)}</td>;
};

const NumberCell = (field: keyof RawSaleRecordDto, formatter: (value?: number | null) => string): React.FC<GridCellProps> => props => {
    const record = props.dataItem as RawSaleRecordDto;
    const value = record[field];

    return <td className="k-table-td text-end">{typeof value === "number" ? formatter(value) : "-"}</td>;
};

const RawSaleRecordManagement: React.FC = () => {
    const [selectedCustomer, setSelectedCustomer] = useState<CustomerDto | null>(null);
    const [selectedRetailer, setSelectedRetailer] = useState<RetailerDto | null>(null);
    const [selectedProduct, setSelectedProduct] = useState<ProductDto | null>(null);
    const [fromDate, setFromDate] = useState<Date | null>(null);
    const [toDateValue, setToDateValue] = useState<Date | null>(null);
    const [dataState, setDataState] = useState<DataSourceRequestState>({ skip: 0, take: 50, sort: initialSort });

    const rawSaleRecordRequest = buildRawSaleRecordRequest(dataState, selectedCustomer, selectedRetailer, selectedProduct, fromDate, toDateValue);
    const retailerRequest: DataSourceRequestState | typeof skipToken = selectedCustomer == null ? skipToken : { ...selectorRequest, filter: { logic: "and", filters: [{ field: "customerId", operator: "eq", value: selectedCustomer.id }] } };
    const productRequest: DataSourceRequestState | typeof skipToken = selectedCustomer == null ? skipToken : { ...selectorRequest, filter: { logic: "and", filters: [{ field: "retailer.customerId", operator: "eq", value: selectedCustomer.id }, ...(selectedRetailer == null ? [] : [{ field: "retailerId", operator: "eq", value: selectedRetailer.id }])] } };

    const { data, isFetching, isError } = useListAdminRawSaleRecordsQuery(rawSaleRecordRequest);
    const { data: customerData, isFetching: isCustomersFetching, isError: isCustomersError } = useListCustomersQuery(selectorRequest);
    const { currentData: retailerData, isFetching: isRetailersFetching, isError: isRetailersError } = useListRetailersQuery(retailerRequest);
    const { currentData: productData, isFetching: isProductsFetching, isError: isProductsError } = useListProductsQuery(productRequest);

    const resetPaging = (): void => setDataState(previous => ({ ...previous, skip: 0 }));
    const handleCustomerChange = (event: DropDownListChangeEvent): void => {
        const customer = event.value as CustomerDto | null;
        setSelectedCustomer(customer?.id === 0 ? null : customer);
        setSelectedRetailer(null);
        setSelectedProduct(null);
        resetPaging();
    };
    const handleRetailerChange = (event: DropDownListChangeEvent): void => {
        const retailer = event.value as RetailerDto | null;
        setSelectedRetailer(retailer?.id === 0 ? null : retailer);
        setSelectedProduct(null);
        resetPaging();
    };
    const handleProductChange = (event: DropDownListChangeEvent): void => {
        const product = event.value as ProductDto | null;
        setSelectedProduct(product?.id === 0 ? null : product);
        resetPaging();
    };
    const handleFromDateChange = (event: DatePickerChangeEvent): void => {
        setFromDate(event.value);
        resetPaging();
    };
    const handleToDateChange = (event: DatePickerChangeEvent): void => {
        setToDateValue(event.value);
        resetPaging();
    };

    const gridData: RawSaleRecordDto[] = data?.data ?? [];
    const emptyMessage = selectedCustomer == null ? "Select a customer to view raw sales." : isError ? "Unable to load raw sales. Please try again." : "No raw sales found for the selected filters.";

    return (
        <div className="d-flex flex-column gap-3 p-3">
            <div className="row g-3 align-items-end">
                <div className="col-12 col-md-3">
                    <label className="form-label fw-semibold" htmlFor="admin-raw-sale-customer">
                        Customer
                    </label>
                    <DropDownList id="admin-raw-sale-customer" data={customerData?.data ?? []} textField="name" dataItemKey="id" value={selectedCustomer} onChange={handleCustomerChange} disabled={isCustomersFetching} defaultItem={{ id: 0, name: isCustomersFetching ? "Loading customers..." : "Select customer" }} />
                </div>
                <div className="col-12 col-md-3">
                    <label className="form-label fw-semibold" htmlFor="admin-raw-sale-retailer">
                        Retailer
                    </label>
                    <DropDownList id="admin-raw-sale-retailer" data={retailerData?.data ?? []} textField="name" dataItemKey="id" value={selectedRetailer} onChange={handleRetailerChange} disabled={selectedCustomer == null || isRetailersFetching} defaultItem={{ id: 0, name: isRetailersFetching ? "Loading retailers..." : "All retailers" }} />
                </div>
                <div className="col-12 col-md-3">
                    <label className="form-label fw-semibold" htmlFor="admin-raw-sale-product">
                        Product
                    </label>
                    <DropDownList id="admin-raw-sale-product" data={productData?.data ?? []} textField="name" dataItemKey="id" value={selectedProduct} onChange={handleProductChange} disabled={selectedCustomer == null || isProductsFetching} defaultItem={{ id: 0, name: isProductsFetching ? "Loading products..." : "All products" }} />
                </div>
                <div className="col-12 col-md-3">
                    <div className="row g-2">
                        <div className="col">
                            <label className="form-label fw-semibold" htmlFor="admin-raw-sale-from-date">
                                From date
                            </label>
                            <DatePicker id="admin-raw-sale-from-date" value={fromDate} onChange={handleFromDateChange} />
                        </div>
                        <div className="col">
                            <label className="form-label fw-semibold" htmlFor="admin-raw-sale-to-date">
                                To date
                            </label>
                            <DatePicker id="admin-raw-sale-to-date" value={toDateValue} onChange={handleToDateChange} />
                        </div>
                    </div>
                </div>
            </div>
            {(isError || isCustomersError || isRetailersError || isProductsError) && <div className="alert alert-danger mb-0" role="alert">Unable to load raw sales, products, retailers, or customers. Please try again.</div>}
            <AppGrid
                showLoader={isFetching || isCustomersFetching || isRetailersFetching || isProductsFetching}
                dataItemKey="id"
                sortable={false}
                filterable={false}
                data={gridData}
                resizable={true}
                initialState={dataState}
                onPageChange={(event: GridPageChangeEvent) => setDataState(previous => ({ ...previous, skip: event.page.skip, take: event.page.take }))}
                onDataStateChange={(event: GridDataStateChangeEvent) => setDataState({ ...event.dataState, skip: event.dataState.skip, take: event.dataState.take })}
                sort={dataState.sort}
                total={data?.total ?? 0}
                skip={dataState.skip}
                take={dataState.take}
                defaultSort={initialSort}
                onSortChange={(event: GridSortChangeEvent) => setDataState(previous => ({ ...previous, sort: event.sort, skip: 0 }))}
                pageable={{ buttonCount: 3, pageSizes: [50, 100, 200] }}
                style={{ height: "100%" }}
            >
                <GridNoRecords>{!isFetching && <span className="text-muted">{emptyMessage}</span>}</GridNoRecords>
                <GridColumn field="productName" title="Product" cells={{ data: TextCell("product_name") }} />
                <GridColumn field="retailer" title="Retailer" width="180px" cells={{ data: TextCell("retailer") }} />
                <GridColumn field="sourceKey" title="External ID" width="200px" cells={{ data: TextCell("external_id") }} />
                <GridColumn field="category" title="Category" width="160px" cells={{ data: TextCell("category") }} />
                <GridColumn field="fromDate" title="From Date" width="150px" cells={{ data: DateCell("from_date") }} />
                <GridColumn field="toDate" title="To Date" width="150px" cells={{ data: DateCell("to_date") }} />
                <GridColumn field="unitsSold" title="Units Sold" width="140px" cells={{ data: NumberCell("units_sold", formatNumber) }} />
                <GridColumn field="totalRevenue" title="Total Revenue" width="160px" cells={{ data: NumberCell("total_revenue", formatCurrency) }} />
            </AppGrid>
        </div>
    );
};

export default RawSaleRecordManagement;
